import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Router} from '@angular/router';
import {
  ButtonComponent,
  DropdownComponent,
  DropdownItemComponent,
  DropdownMenuComponent,
  DropdownTriggerComponent,
  MODAL_DATA,
  ModalRef,
  TextareaComponent,
  ToggleComponent,
} from '@sms-ir/ui-components';

import {SharedService} from '@shared/services/shared.service';
import {UtilityService} from '@shared/services/utility.service';
import {ToastifyService} from '@shared/services/toastify.service';
import {AppNotifications} from '@shared/enums/app-notifications.enum';
import {FilterItemModel} from '@shared/models/filter/filter-item.model';

import {XmaApiService} from '@app/core/services/xma-api.service';
import type {XmaBusinessProfileResult} from '@app/core/interfaces/xma-business-profile-result.interface';
import type {XmaTextGenerationBody} from '@app/core/interfaces/xma-text-generation-body.interface';
import {PersonalizationOption, PERSONALIZATION_OPTIONS} from '@shared/components/text-generation-modal/models/personalization-option.model';

export interface TextGenerationModalData {
  currentText?: string;
  minLength?: number;
}

export interface TextGenerationModalResult {
  action: 'replace';
  text: string;
}

@UntilDestroy()
@Component({
  selector: 'app-text-generation-modal',
  templateUrl: './text-generation-modal.component.html',
  styleUrls: ['./text-generation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    DropdownComponent,
    DropdownTriggerComponent,
    DropdownMenuComponent,
    DropdownItemComponent,
    TextareaComponent,
    ToggleComponent,
  ],
})
export class TextGenerationModalComponent implements OnInit, OnDestroy {
  private readonly modalRef = inject<ModalRef<TextGenerationModalResult | false>>(ModalRef);
  private readonly modalData = inject<TextGenerationModalData | null>(MODAL_DATA, {optional: true});
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly sharedService = inject(SharedService);
  private readonly xmaService = inject(XmaApiService);
  private readonly toastifyService = inject(ToastifyService);
  private readonly utilityService = inject(UtilityService);
  private readonly router = inject(Router);

  readonly currentText = this.modalData?.currentText ?? '';
  readonly minLength = this.modalData?.minLength ?? 15;

  selectedStyle: number | null = null;
  selectedGoal: number | null = null;
  selectedLength: number | null = null;

  previewText = '';

  isLoading = false;
  isGenerated = false;

  generatedText = '';
  private fullGeneratedText = '';
  private typingTimer: ReturnType<typeof setInterval> | null = null;

  typingSpeed = 30;

  generatedSmsCount = 0;
  generatedLocalSmsLen = 0;
  generatedContainer = 70;

  styleOptions: FilterItemModel[] = [];
  goalOptions: FilterItemModel[] = [];
  lengthOptions: FilterItemModel[] = [];

  isPersonalizationPanelOpen = false;
  isProfileLoading = false;
  isProfileLoaded = false;
  private shouldRefreshProfileOnFocus = false;

  personalizationOptions: PersonalizationOption[] = PERSONALIZATION_OPTIONS.map(opt => ({...opt}));

  @ViewChild('personalizationContainer', {static: false})
  personalizationContainer!: ElementRef<HTMLElement>;

  get isTyping(): boolean {
    return !!this.typingTimer;
  }

  ngOnInit(): void {
    this.styleOptions = this.sharedService.textStyle;
    this.goalOptions = this.sharedService.textGoal;
    this.lengthOptions = this.sharedService.textLength;
    this.previewText = this.currentText || '';
    this.loadBusinessProfile();
  }

  ngOnDestroy(): void {
    if (this.typingTimer) {
      clearInterval(this.typingTimer);
    }
  }

  togglePersonalizationPanel(): void {
    this.isPersonalizationPanelOpen = !this.isPersonalizationPanelOpen;

    if (this.isPersonalizationPanelOpen && this.shouldRefreshProfileOnFocus && !this.isProfileLoading) {
      this.shouldRefreshProfileOnFocus = false;
      this.loadBusinessProfile();
      return;
    }

    if (this.isPersonalizationPanelOpen && !this.isProfileLoaded && !this.isProfileLoading) {
      this.loadBusinessProfile();
    }
  }

  loadBusinessProfile(): void {
    this.isProfileLoading = true;
    this.changeDetectorRef.markForCheck();

    this.xmaService.getBusinessProfile()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.success && res.result) {
            this.mapProfileToOptions(res.result);
          }
          this.isProfileLoading = false;
          this.isProfileLoaded = true;
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.isProfileLoading = false;
          this.isProfileLoaded = true;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  private mapProfileToOptions(profile: XmaBusinessProfileResult): void {
    this.personalizationOptions.forEach(opt => {
      const field = profile[opt.key];
      if (field) {
        const value = field.value?.trim();
        opt.value = value || undefined;
        opt.isActive = field.isUsed && !!value;
      }
    });
    this.changeDetectorRef.markForCheck();
  }

  get hasBusinessProfileData(): boolean {
    return this.personalizationOptions.some(opt => !!opt.value);
  }

  get isProfileFullyCompleted(): boolean {
    return this.personalizationOptions.every(opt => !!opt.value);
  }

  get activePersonalizationOptions(): PersonalizationOption[] {
    return this.personalizationOptions.filter(opt => opt.isActive && !!opt.value);
  }

  get hasActivePersonalization(): boolean {
    return this.activePersonalizationOptions.length > 0;
  }

  onRemovePersonalizationChip(opt: PersonalizationOption): void {
    opt.isActive = false;
    this.changeDetectorRef.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (
      this.isPersonalizationPanelOpen &&
      this.personalizationContainer &&
      !this.personalizationContainer.nativeElement.contains(event.target as Node)
    ) {
      this.isPersonalizationPanelOpen = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  @HostListener('window:focus')
  onWindowFocus(): void {
    if (!this.shouldRefreshProfileOnFocus || this.isProfileLoading) {
      return;
    }
    this.shouldRefreshProfileOnFocus = false;
    this.loadBusinessProfile();
  }

  onPersonalizationToggleChange(opt: PersonalizationOption, isActive: boolean): void {
    if (!opt.value) return;
    opt.isActive = isActive;
    this.changeDetectorRef.markForCheck();
  }

  private buildGenerationBody(): XmaTextGenerationBody {
    const body: XmaTextGenerationBody = {
      text: this.previewText,
      style: this.selectedStyle,
      length: this.selectedLength,
      goal: this.selectedGoal,
    };
    this.personalizationOptions.forEach(opt => {
      if (!opt.isActive || !opt.value) return;
      body[opt.key] = opt.value;
    });
    return body;
  }

  onGenerate(): void {
    if (this.isLoading || !this.previewText.trim()) {
      this.toastifyService.error(AppNotifications.PleaseEnterText);
      return;
    }
    this.isLoading = true;
    this.isGenerated = false;
    this.generatedText = '';
    this.fullGeneratedText = '';
    this.xmaService.textGeneration(this.buildGenerationBody())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.isLoading = false;
          if (!res?.success || !res?.result) {
            return;
          }
          this.fullGeneratedText = res.result;
          this.isGenerated = true;
          this.startTyping();
        },
        error: () => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  startTyping(): void {
    if (this.typingTimer) {
      clearInterval(this.typingTimer);
      this.typingTimer = null;
    }
    let index = 0;
    this.typingTimer = setInterval(() => {
      if (index >= this.fullGeneratedText.length) {
        clearInterval(this.typingTimer!);
        this.typingTimer = null;
        this.changeDetectorRef.markForCheck();
        return;
      }
      this.generatedText += this.fullGeneratedText[index++];
      this.calculateSmsCount(this.generatedText);
      this.changeDetectorRef.markForCheck();
    }, this.typingSpeed);
  }

  onReplace(): void {
    if (this.generatedText) {
      this.modalRef.close({action: 'replace', text: this.generatedText});
    }
  }

  onCopy(text: string): void {
    navigator.clipboard.writeText(text).then(() =>
      this.toastifyService.success(AppNotifications.Copy),
    );
  }

  onClose(): void {
    if (this.typingTimer) {
      clearInterval(this.typingTimer);
    }
    this.modalRef.close(false);
  }

  isGenerateDisabled(): boolean {
    return this.isLoading || !this.previewText.trim() || this.previewText.length < this.minLength;
  }

  formatCount(value: number): string {
    return new Intl.NumberFormat('fa-IR').format(value);
  }

  calculateSmsCount(text: string): void {
    const len = text.length;
    const unicode = this.utilityService.containsNonLatinCodepoints(text);
    const sizes = unicode
      ? {repeat: 67, first: 70, second: 134, third: 201}
      : {repeat: 153, first: 160, second: 306, third: 459};

    if (len <= sizes.first) {
      this.generatedSmsCount = len ? 1 : 0;
      this.generatedLocalSmsLen = len;
      this.generatedContainer = sizes.first;
      return;
    }
    if (len <= sizes.second) {
      this.generatedSmsCount = 2;
      this.generatedLocalSmsLen = len - sizes.first;
      this.generatedContainer = sizes.second - sizes.first;
      return;
    }
    if (len <= sizes.third) {
      this.generatedSmsCount = 3;
      this.generatedLocalSmsLen = len - sizes.second;
      this.generatedContainer = sizes.repeat;
      return;
    }
    this.generatedSmsCount = 4 + Math.floor((len - sizes.third - 1) / sizes.repeat);
    this.generatedLocalSmsLen = len - (sizes.third + (this.generatedSmsCount - 4) * sizes.repeat);
    this.generatedContainer = sizes.repeat;
  }

  navigateToBusinessInfo(): void {
    const urlTree = this.router.createUrlTree(['/account', 'profile', 'business-info']);
    const url = this.router.serializeUrl(urlTree);
    this.isPersonalizationPanelOpen = false;
    this.shouldRefreshProfileOnFocus = true;
    window.open(url, '_blank');
  }
}
